/** Differences reported by the latest comparison, independent of whether annotations were drawn. */

export interface CadChange {
  id: string;
  note: string;
  kind?: string;
  layer?: string;
  distance?: number;
  textBefore?: string;
  textAfter?: string;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface CadReview {
  changes: CadChange[];
  annotationStatus: "not_requested" | "incomplete" | "completed" | null;
  sessionId: string | null;
  documentId: string | null;
}

interface StepWithResult {
  id?: string;
  tool: string;
  result?: { success?: boolean; rawResult?: unknown; display?: Record<string, unknown> };
}

export function cadChangeViewRequest(review: CadReview, selectedSession: string | null, change: CadChange) {
  if (!selectedSession || !review.sessionId || !review.documentId || selectedSession !== review.sessionId) return null;
  const { minX, minY, maxX, maxY } = change;
  return { target_sessions: { autocad: review.sessionId }, documentId: review.documentId, minX, minY, maxX, maxY };
}

export function cadReviewFromMessages(messages: readonly { role: string; steps?: readonly StepWithResult[] }[]): CadReview {
  const steps: StepWithResult[] = [];
  const positions = new Map<string, number>();
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    for (const step of message.steps ?? []) {
      const key = step.id ? `${step.tool}:${step.id}` : null;
      const position = key ? positions.get(key) : undefined;
      if (position !== undefined) steps[position] = step;
      else {
        if (key) positions.set(key, steps.length);
        steps.push(step);
      }
    }
  }
  return cadReviewFromSteps(steps);
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function oneChange(value: unknown): CadChange | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = record["id"];
  if (typeof id !== "string" || id.length === 0) return null;
  if (!finite(record["minX"]) || !finite(record["minY"]) || !finite(record["maxX"]) || !finite(record["maxY"])) {
    return null;
  }
  if (record["minX"] > record["maxX"] || record["minY"] > record["maxY"]) return null;
  const note = typeof record["note"] === "string" && record["note"].length > 0 ? record["note"] : id;
  return {
    id,
    note,
    ...Object.fromEntries(["kind", "layer", "textBefore", "textAfter"].filter(key => typeof record[key] === "string").map(key => [key, record[key]])),
    ...(finite(record["distance"]) ? { distance: record["distance"] } : {}),
    minX: record["minX"],
    minY: record["minY"],
    maxX: record["maxX"],
    maxY: record["maxY"],
  };
}

/** A failed, pending, or misaligned retry supersedes earlier comparisons too. */
export function cadReviewFromSteps(steps: readonly StepWithResult[] | undefined): CadReview {
  const empty: CadReview = { changes: [], annotationStatus: null, sessionId: null, documentId: null };
  if (!steps) return empty;
  let index = -1;
  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i]!;
    if (step.tool === "run_cad_procedure") { index = i; break; }
  }
  const latest = steps[index];
  if (!latest || latest.result?.success !== true) return empty;
  const raw = record(latest.result.rawResult);
  if (!raw || raw["success"] === false || raw["misaligned"] === true || !Array.isArray(raw["changes"])) return empty;
  const found: CadChange[] = [];
  const ids = new Set<string>();
  for (const item of raw["changes"]) {
    const change = oneChange(item);
    if (change && !ids.has(change.id)) { found.push(change); ids.add(change.id); }
  }
  const comparison = latest.result.display;
  const binding = {
    sessionId: typeof comparison?.["sessionId"] === "string" && comparison["sessionId"].trim() ? comparison["sessionId"] : null,
    documentId: typeof comparison?.["documentId"] === "string" && comparison["documentId"].trim() ? comparison["documentId"] : null,
  };
  if (found.length > 0 && typeof comparison?.["comparisonId"] === "string") {
    for (let i = steps.length - 1; i > index; i--) {
      const step = steps[i]!;
      if (step.tool !== "report_cad_review" || step.result?.success !== true) continue;
      const review = record(step.result.rawResult);
      if (!review || review["comparisonId"] !== comparison["comparisonId"] ||
          review["documentId"] !== comparison["documentId"] || review["sessionId"] !== comparison["sessionId"] ||
          !Array.isArray(review["changes"])) continue;
      const changes = review["changes"].map(oneChange);
      if (changes.some(c => c === null) || new Set(changes.map(c => c?.id)).size !== changes.length) continue;
      if (!review["changes"].every(item => {
        const c = record(item)!;
        const sources = c["sourceIds"];
        return Array.isArray(sources) && sources.every(id => typeof id === "string" && ids.has(id)) &&
          (sources.length > 0 || (typeof c["investigation"] === "string" && c["investigation"].trim().length > 0));
      })) continue;
      const status = review["annotationStatus"];
      if (status !== "completed" && status !== "incomplete" && status !== "not_requested") continue;
      let annotationStatus: NonNullable<CadReview["annotationStatus"]> = status;
      if (annotationStatus === "completed") {
        // Reporting a list is not evidence that a gated drawing write ran.
        const executed = steps.slice(index + 1, i).some(s => {
          const display = s.result?.display;
          return s.tool === "send_code_to_autocad" && s.result?.success === true &&
            record(s.result.rawResult)?.["success"] === true && display?.["intent"] === "deliver" &&
            typeof review["annotationExecutionId"] === "string" && display["executionId"] === review["annotationExecutionId"] &&
            display["documentId"] === comparison["documentId"] && display["sessionId"] === comparison["sessionId"];
        });
        if (!executed || changes.length === 0) annotationStatus = "incomplete";
      }
      return { changes: changes as CadChange[], annotationStatus, ...binding };
    }
  }
  return { changes: found, annotationStatus: null, ...binding };
}

export function cadChangeLabel(change: CadChange, t: (key: string) => string): string {
  if (change.note !== change.id) return change.note.startsWith(change.id) ? change.note : `${change.id} ${change.note}`;
  if (!change.kind) return change.id;
  const kind = ["added", "deleted", "moved", "text", "mixed"].includes(change.kind) ? change.kind : "modified";
  let detail = t(`cad.kind.${kind}`);
  if (kind === "moved" && change.distance !== undefined) detail += ` ${change.distance}`;
  if (kind === "text") detail += `: ${(change.textBefore ?? "").slice(0, 18)} → ${(change.textAfter ?? "").slice(0, 18)}`;
  return `${change.id} ${detail}${change.layer ? ` (${change.layer})` : ""}`;
}
